import { useState, useCallback } from "react";
import { useParams } from "react-router";
import { Button, Select, Input, Table, Popconfirm, DatePicker } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useTrialPlans } from "@/hooks/useTrialPlans";
import type { ProtocolDoc, ProtocolDocType } from "./types";

const { TextArea } = Input;

const DOC_TYPE_OPTIONS: { value: ProtocolDocType; label: string }[] = [
  { value: "Study Protocol", label: "Study Protocol" },
  { value: "Informed Consent", label: "Informed Consent" },
  { value: "SAP", label: "Statistical Analysis Plan" },
  { value: "Investigator Brochure", label: "Investigator Brochure" },
  { value: "Other", label: "Other" },
];

const DOC_TYPE_BADGE: Record<ProtocolDocType, string> = {
  "Study Protocol": "bg-[#D8EEF8] text-[#196A9F]",
  "Informed Consent": "bg-[#E0DEF0] text-[#59528F]",
  SAP: "bg-[#CFE5D7] text-[#486563]",
  "Investigator Brochure": "bg-[#F8EEE1] text-[#C06119]",
  Other: "bg-[#F3F4F6] text-[#6B7280]",
};

interface EditableFieldProps {
  label: string;
  value: string;
  onSave: (v: string) => void;
  placeholder?: string;
  rows?: number;
}

function EditableField({
  label,
  value,
  onSave,
  placeholder,
  rows = 3,
}: EditableFieldProps) {
  const [draft, setDraft] = useState(value);

  const handleBlur = useCallback(() => {
    if (draft !== value) onSave(draft);
  }, [draft, value, onSave]);

  return (
    <div className="mb-5">
      <p className="text-[12px] font-semibold uppercase tracking-wide text-[#6B7280] mb-1.5">
        {label}
      </p>
      <TextArea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder ?? `Enter ${label.toLowerCase()}…`}
        autoSize={{ minRows: rows, maxRows: rows + 4 }}
        style={{ fontSize: 13, color: "#1F2933", borderColor: "#E5E7EB" }}
      />
    </div>
  );
}

const EMPTY_DOC: Omit<ProtocolDoc, "id"> = {
  docType: "Study Protocol",
  title: "",
  version: "",
  date: "",
  reference: "",
  notes: "",
};

export default function TrialPlanningHome() {
  const { planId } = useParams<{ planId: string }>();
  const { getPlan, updatePlan } = useTrialPlans();
  const plan = planId ? getPlan(planId) : undefined;

  const [addingDoc, setAddingDoc] = useState(false);
  const [newDoc, setNewDoc] = useState<Omit<ProtocolDoc, "id">>(EMPTY_DOC);

  if (!plan) return null;

  const protocols = plan.protocols ?? [];

  const save = (changes: Partial<typeof plan>) => updatePlan(plan.id, changes);

  const addDoc = () => {
    if (!newDoc.title.trim()) return;
    save({ protocols: [...protocols, { ...newDoc, id: crypto.randomUUID() }] });
    setNewDoc(EMPTY_DOC);
    setAddingDoc(false);
  };

  const removeDoc = (docId: string) =>
    save({ protocols: protocols.filter((d) => d.id !== docId) });

  const columns: ColumnsType<ProtocolDoc> = [
    {
      title: "Type",
      key: "docType",
      width: 150,
      render: (_, doc) => (
        <span
          className={`inline-flex items-center h-5 rounded-[4px] px-1.5 text-[12px] font-semibold ${DOC_TYPE_BADGE[doc.docType]}`}
        >
          {doc.docType}
        </span>
      ),
    },
    {
      title: "Title",
      key: "title",
      render: (_, doc) => (
        <span className="text-[13px] font-medium text-[#1F2933]">
          {doc.title}
        </span>
      ),
    },
    {
      title: "Version",
      key: "version",
      width: 80,
      render: (_, doc) => (
        <span className="text-[12px] text-[#6B7280]">{doc.version || "—"}</span>
      ),
    },
    {
      title: "Date",
      key: "date",
      width: 100,
      render: (_, doc) => (
        <span className="text-[12px] text-[#6B7280]">
          {doc.date
            ? new Date(doc.date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : "—"}
        </span>
      ),
    },
    {
      title: "Reference",
      key: "reference",
      width: 120,
      render: (_, doc) =>
        doc.reference ? (
          <a
            href={doc.reference}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[12px] text-[#0C98D0] hover:underline truncate max-w-[110px] inline-block"
          >
            {doc.reference.startsWith("http") ? "Open link" : doc.reference}
          </a>
        ) : (
          <span className="text-[#D1D5DB] text-[12px]">—</span>
        ),
    },
    {
      title: "Notes",
      key: "notes",
      render: (_, doc) => (
        <span className="text-[12px] text-[#6B7280]">{doc.notes || ""}</span>
      ),
    },
    {
      key: "actions",
      width: 40,
      align: "center",
      render: (_, doc) => (
        <Popconfirm
          title="Remove this document?"
          okText="Remove"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
          onConfirm={() => removeDoc(doc.id)}
        >
          <Button
            size="small"
            type="text"
            icon={<DeleteOutlined />}
            className="text-[#9CA3AF] hover:text-[#EF4444]"
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="overflow-y-auto h-full">
      <div className="grid grid-cols-2 gap-6 pb-6">
        {/* Left column: trial description + eligibility criteria */}
        <div>
          <div className="bg-white rounded-[10px] border border-[#F0F2F4] p-5">
            <h3 className="text-[15px] font-semibold text-[#1F2933] mb-4 m-0">
              Trial Description
            </h3>
            <EditableField
              label="Trial Name"
              value={plan.name ?? ""}
              onSave={(v) => save({ name: v })}
              placeholder="Full trial name or protocol title…"
              rows={1}
            />
            <EditableField
              label="Sponsor"
              value={plan.sponsor ?? ""}
              onSave={(v) => save({ sponsor: v })}
              placeholder="Sponsoring organization…"
              rows={1}
            />
            <EditableField
              label="Overview"
              value={plan.overview ?? ""}
              onSave={(v) => save({ overview: v })}
              placeholder="Brief description of this trial's purpose, design, and context…"
              rows={4}
            />
            <EditableField
              label="Primary Objective"
              value={plan.objective ?? ""}
              onSave={(v) => save({ objective: v })}
              placeholder="The main scientific or clinical question this trial addresses…"
              rows={3}
            />
            <EditableField
              label="Primary Endpoint"
              value={plan.primaryEndpoint ?? ""}
              onSave={(v) => save({ primaryEndpoint: v })}
              placeholder="The primary outcome measure used to assess the main objective…"
              rows={2}
            />
            <EditableField
              label="Key Inclusion Criteria"
              value={plan.inclusionCriteria ?? ""}
              onSave={(v) => save({ inclusionCriteria: v })}
              placeholder="e.g. Age ≥ 18, confirmed diagnosis, adequate organ function…"
              rows={4}
            />
            <EditableField
              label="Key Exclusion Criteria"
              value={plan.exclusionCriteria ?? ""}
              onSave={(v) => save({ exclusionCriteria: v })}
              placeholder="e.g. Prior treatment, active comorbidities, pregnancy…"
              rows={4}
            />
          </div>
        </div>

        {/* Right column: draft trial documents */}
        <div>
          <div className="bg-white rounded-[10px] border border-[#F0F2F4] p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[15px] font-semibold text-[#1F2933] m-0">
                Draft Trial Documents
              </h3>
              {!addingDoc && (
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  className="bg-[#0C98D0] border-none text-white"
                  onClick={() => setAddingDoc(true)}
                >
                  Add document
                </Button>
              )}
            </div>

            {protocols.length === 0 && !addingDoc && (
              <div className="flex flex-col items-center justify-center py-10 rounded-[8px] border border-dashed border-[#D1D5DB]">
                <FileTextOutlined className="text-[24px] text-[#D1D5DB] mb-2" />
                <p className="text-[14px] text-[#6B7280] text-center">
                  No documents yet. Add protocol versions, ICFs, or other study
                  documents.
                </p>
                <Button
                  size="small"
                  type="primary"
                  className="mt-3 bg-[#0C98D0] border-none"
                  onClick={() => setAddingDoc(true)}
                >
                  Add first document
                </Button>
              </div>
            )}

            {addingDoc && (
              <div className="rounded-[8px] border border-[#0C98D0]/30 bg-[#F0FBFF] p-4 mb-4">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <p className="text-[12px] text-[#6B7280] mb-1">
                      Document type
                    </p>
                    <Select
                      size="small"
                      options={DOC_TYPE_OPTIONS}
                      value={newDoc.docType}
                      onChange={(v) => setNewDoc((d) => ({ ...d, docType: v }))}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <p className="text-[12px] text-[#6B7280] mb-1">Version</p>
                    <Input
                      size="small"
                      placeholder="e.g. v2.1"
                      value={newDoc.version}
                      onChange={(e) =>
                        setNewDoc((d) => ({ ...d, version: e.target.value }))
                      }
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <p className="text-[12px] text-[#6B7280] mb-1">Title</p>
                  <Input
                    size="small"
                    placeholder="Document title"
                    value={newDoc.title}
                    onChange={(e) =>
                      setNewDoc((d) => ({ ...d, title: e.target.value }))
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <p className="text-[12px] text-[#6B7280] mb-1">Date</p>
                    <DatePicker
                      size="small"
                      className="w-full"
                      value={newDoc.date ? dayjs(newDoc.date) : null}
                      onChange={(d) =>
                        setNewDoc((prev) => ({
                          ...prev,
                          date: d ? d.format("YYYY-MM-DD") : "",
                        }))
                      }
                    />
                  </div>
                  <div>
                    <p className="text-[12px] text-[#6B7280] mb-1">
                      Reference / link
                    </p>
                    <Input
                      size="small"
                      placeholder="URL or document ID"
                      value={newDoc.reference}
                      onChange={(e) =>
                        setNewDoc((d) => ({ ...d, reference: e.target.value }))
                      }
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <p className="text-[12px] text-[#6B7280] mb-1">Notes</p>
                  <Input
                    size="small"
                    placeholder="Optional notes"
                    value={newDoc.notes}
                    onChange={(e) =>
                      setNewDoc((d) => ({ ...d, notes: e.target.value }))
                    }
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    size="small"
                    onClick={() => {
                      setAddingDoc(false);
                      setNewDoc(EMPTY_DOC);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    className="bg-[#0C98D0] border-none"
                    disabled={!newDoc.title.trim()}
                    onClick={addDoc}
                  >
                    Add document
                  </Button>
                </div>
              </div>
            )}

            {protocols.length > 0 && (
              <Table<ProtocolDoc>
                dataSource={protocols}
                columns={columns}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: "max-content" }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
